import {
  useSchedulerChannels,
  useSchedulerConfig,
  useSchedulerState,
  type ChannelScheduleStateOut,
  type SchedulerConfigOut,
  type SchedulerStateOut,
} from '@/api/endpoints/scheduler'

export type SchedulersResult = (
  | { isPending: true; isError: false; error: null; config: null; state: null; channels: null }
  | { isPending: false; isError: true; error: unknown; config: null; state: null; channels: null }
  | {
      isPending: false
      isError: false
      error: null
      config: SchedulerConfigOut
      state: SchedulerStateOut
      channels: Record<string, ChannelScheduleStateOut>
    }
) & {
  /** Refetches the queries that failed (the error state's "Tentar de novo"). */
  refetch: () => void
}

/**
 * Fan-out loader: the scheduler page needs config + state + channel list
 * together. Returns a discriminated union so the caller can narrow once
 * and then treat the data as non-null.
 */
export function useSchedulers(): SchedulersResult {
  const config = useSchedulerConfig()
  const state = useSchedulerState()
  const channels = useSchedulerChannels()
  const refetch = () => {
    for (const query of [config, state, channels]) {
      if (query.isError) void query.refetch()
    }
  }

  if (config.isPending || state.isPending || channels.isPending) {
    return {
      isPending: true,
      isError: false,
      error: null,
      config: null,
      state: null,
      channels: null,
      refetch,
    }
  }
  if (config.isError || state.isError || channels.isError) {
    return {
      isPending: false,
      isError: true,
      error: config.error ?? state.error ?? channels.error,
      config: null,
      state: null,
      channels: null,
      refetch,
    }
  }
  return {
    isPending: false,
    isError: false,
    error: null,
    config: config.data,
    state: state.data,
    channels: channels.data,
    refetch,
  }
}
